import type { MapSpotColorsSettings } from "@gettactic/api";
import {
  MAP_SPOT_COLORS,
  MapSpotColorName
} from "@gettactic/components/themes/mapSpotColors";

/**
 * Bridges organization theme settings (theme_settings.map_spot_colors) to the
 * `--map-spot-*` CSS variables both map pipelines consume. Row colors cascade
 * to every token of that status row; token overrides pin individual colors.
 */

export type MapSpotRowKey =
  | "available"
  | "approval"
  | "assigned"
  | "walkUp"
  | "unavailable"
  | "checkedIn";

export type MapSpotRowSecondaryColor = {
  /** Registry token the secondary picker pins (stored in `overrides`) */
  token: MapSpotColorName;
  label: string;
};

export type MapSpotRowDefinition = {
  key: MapSpotRowKey;
  label: string;
  /** Registry tokens this row's color is written to */
  tokens: MapSpotColorName[];
  /** Label of the row's main picker (defaults to "Fill") */
  primaryLabel?: string;
  /** Optional second color of the row (usually the border) */
  secondary?: MapSpotRowSecondaryColor;
};

export const MAP_SPOT_ROWS: MapSpotRowDefinition[] = [
  {
    key: "available",
    label: "Available",
    tokens: ["availableColor"],
    secondary: { token: "spotFullyAvailableStroke", label: "Border" }
  },
  {
    key: "approval",
    label: "Approval",
    tokens: ["spotRequiresApprovalFill"],
    secondary: { token: "spotRequiresApprovalStroke", label: "Border" }
  },
  {
    key: "assigned",
    label: "Assigned",
    tokens: ["spotNotBookeableFill"],
    secondary: { token: "spotNotBookeableStroke", label: "Border" }
  },
  {
    key: "walkUp",
    label: "Walk-up",
    tokens: ["spotWalkUpStroke"],
    primaryLabel: "Border",
    secondary: { token: "spotWalkUpFill", label: "Fill" }
  },
  {
    key: "unavailable",
    label: "Unavailable / Assigned to others",
    tokens: ["spotNotBookeableUnavailableFill", "spotNotAssignedFill"],
    secondary: { token: "spotNotBookeableUnavailableStroke", label: "Border" }
  },
  {
    key: "checkedIn",
    label: "Checked-in ring",
    tokens: ["spotCheckedInStroke"]
  }
];

/** Registry tokens whose default value follows another token (CSS var alias) */
const TOKEN_DEFAULT_ALIASES: Partial<
  Record<MapSpotColorName, MapSpotColorName>
> = {
  spotFullyAvailableFill: "availableColor",
  spotBookeableWithSchedulesFill: "availableColor",
  spotNotAssignedStroke: "availableColor",
  spotWalkUpStroke: "availableColor"
};

/**
 * Returns only the CSS variable assignments the org actually customized,
 * so everything else keeps following the theme defaults.
 */
export const resolveMapSpotCssVariables = (
  settings?: MapSpotColorsSettings
): Record<string, string> => {
  const cssVariables: Record<string, string> = {};

  for (const row of MAP_SPOT_ROWS) {
    const rowColor = settings?.rows?.[row.key];
    if (!rowColor) continue;
    for (const token of row.tokens) {
      cssVariables[MAP_SPOT_COLORS[token].cssVar] = rowColor;
    }
    if (row.key === "walkUp") {
      cssVariables[MAP_SPOT_COLORS.spotWalkUpFill.cssVar] = rowColor;
    }
  }

  for (const [token, color] of Object.entries(settings?.overrides ?? {})) {
    const definition = MAP_SPOT_COLORS[token as MapSpotColorName];
    if (definition && color) {
      cssVariables[definition.cssVar] = color;
    }
  }

  return cssVariables;
};

/**
 * Resolves every registry token to a displayable color (override > row >
 * registry default), used by the settings matrix preview.
 */
export const resolveMapSpotDisplayColors = (
  settings?: MapSpotColorsSettings
): Record<MapSpotColorName, string> => {
  const displayColors = {} as Record<MapSpotColorName, string>;

  for (const name of Object.keys(MAP_SPOT_COLORS) as MapSpotColorName[]) {
    const aliasTarget = TOKEN_DEFAULT_ALIASES[name];
    displayColors[name] = aliasTarget
      ? MAP_SPOT_COLORS[aliasTarget].defaultValue
      : MAP_SPOT_COLORS[name].defaultValue;
  }

  for (const row of MAP_SPOT_ROWS) {
    const rowColor = settings?.rows?.[row.key];
    if (!rowColor) continue;
    for (const token of row.tokens) {
      displayColors[token] = rowColor;
    }
    if (row.key === "available") {
      for (const [token, aliasTarget] of Object.entries(
        TOKEN_DEFAULT_ALIASES
      )) {
        if (aliasTarget === "availableColor") {
          displayColors[token as MapSpotColorName] = rowColor;
        }
      }
    }
    if (row.key === "walkUp") {
      displayColors.spotWalkUpStroke = rowColor;
      displayColors.spotWalkUpFill = rowColor;
    }
  }

  for (const [token, color] of Object.entries(settings?.overrides ?? {})) {
    if (token in MAP_SPOT_COLORS && color) {
      displayColors[token as MapSpotColorName] = color;
    }
  }

  return displayColors;
};

const forEachMapSpotStyleTarget = (
  callback: (style: CSSStyleDeclaration) => void
) => {
  if (typeof document === "undefined") return;
  // documentElement so getComputedStyle-based readers (map-builder) see the
  // values, body so they win the cascade over ThemeProvider inline vars
  callback(document.documentElement.style);
  callback(document.body.style);
};

/** Removes every map spot variable applied by applyMapSpotColors */
export const clearMapSpotColors = (): void => {
  forEachMapSpotStyleTarget((style) => {
    for (const { cssVar } of Object.values(MAP_SPOT_COLORS)) {
      style.removeProperty(cssVar);
    }
  });
};

export const applyMapSpotColors = (settings?: MapSpotColorsSettings): void => {
  // resolveMapSpotCssVariables only returns tokens the org currently
  // customizes — clear first so a removed/reduced override, or a switch to
  // an org/settings state with none, doesn't leave the previous org's
  // colors applied until a full page reload.
  clearMapSpotColors();
  const cssVariables = resolveMapSpotCssVariables(settings);
  forEachMapSpotStyleTarget((style) => {
    for (const [name, value] of Object.entries(cssVariables)) {
      style.setProperty(name, value);
    }
  });
};
